import React, { useRef } from 'react';
import {
  NavItem,
  NavTitle,
  NavbarContainer,
  StepIndicator,
} from './visitor-log.styled';
import { IndicatorGroup } from '../../../motion';
import { Text, font } from '../../../style-utils/text';
import { RightPointer } from '../../icons/right-pointer';
import { NavigateFunction } from 'react-router-dom';
import { getTitlesFromRoute } from './navbar-controller';
import { palette } from '../../../style-utils/theme';

interface NavbarProps {
  hasContent: boolean;
  currentPage: string;
  navigate: NavigateFunction;
  isLog: boolean;
  state: object;
  navigation: string[];
  path: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  hasContent,
  navigate,
  isLog,
  navigation,
  currentPage,
  path,
}) => {
  /* The furthest step reached in THIS flow. A ref rather than state: it changes
     only as a side effect of navigation and nothing renders differently for the
     value itself. It resets when the flow changes, so a new entry cannot
     inherit the last one's reach. */
  const furthestRef = useRef({ flow: '', index: -1 });
  const flowKey = navigation?.join('|') || '';
  const positionNow = navigation
    ? navigation.findIndex(
        route => getTitlesFromRoute(route) === getTitlesFromRoute(currentPage)
      )
    : -1;

  if (furthestRef.current.flow !== flowKey) {
    furthestRef.current = { flow: flowKey, index: positionNow };
  } else if (positionNow > furthestRef.current.index) {
    furthestRef.current.index = positionNow;
  }

  const furthestIndex = furthestRef.current.index;

  return (
    <NavbarContainer>
      <NavTitle>
        {/* AN <h1> ON /main-menu ONLY, AND THE CONDITION IS THE POINT.
            Measured on the running app: the main menu rendered exactly one
            heading of any level - the shift card's <h2>Your shift</h2> - and the
            clock-in gate rendered none at all. A top-level view whose title is
            not a heading gives a screen-reader user nothing to jump to and
            starts the document outline at level 2.

            On the WIZARD routes this component is not the page's title. Each of
            those pages already renders its own <h1> (daily-log.tsx, and the step
            titles on /visitor-log), and promoting this one as well produced TWO
            level-1 headings per page - on /daily-log, two with identical text.
            Two h1s is a worse outline than none, so this stays a <p> there,
            exactly as it was, and those pages keep their own heading.

            Straightening out the wizard flow-title-vs-step-title hierarchy is a
            real change across 51 routes and is deliberately NOT bundled here.

            `as` ONLY, no styling change: Text takes its font-size, weight and
            margins from the props below (mt/mb are 0), so every default an h1
            would bring is already overridden. The gate's title is an h1 for the
            same reason (main-menu.styled.tsx GateTitle) and the two can never
            render together - the menu column is not mounted while a gate is up. */}
        <Text
          as={path === '/main-menu' ? 'h1' : 'p'}
          mb={0}
          mt={0}
          fontFamily={font.family}
          fontWeight={font.weight.bold}
          fontSize={font.size.xl}
        >
          {path === '/main-menu'
            ? 'Main Menu'
            : isLog || path?.includes('/daily-log')
            ? 'Building Activities'
            : hasContent || path?.includes('/visitor-log')
            ? 'Visitor Log'
            : 'Choose type'}
        </Text>
        {path === '/main-menu' && (
          <Text mt={8} color={font.color.gray} fontSize={font.size.xs}>
            Please select one of the options below
          </Text>
        )}
      </NavTitle>
      {/* One accent bar for the whole step list, scoped by the group so the
          desk rail's bar (a different IndicatorGroup) never shares it. */}
      <IndicatorGroup id="wizard-steps">
      {hasContent &&
        navigation?.map((e, i) => {
          const currentIndex = navigation.findIndex(
            route =>
              getTitlesFromRoute(route) === getTitlesFromRoute(currentPage)
          );
          const isActive = i === currentIndex;
          const isComplete = currentIndex > -1 && i < currentIndex;
          /* NAVIGABLE = ALREADY VISITED, not "before where I am standing".
             See the note at the top of this file: `isComplete` shrinks the
             moment the reader steps back, which locked the steps they had just
             answered. `furthestIndex` only grows, so going back to correct
             something never costs the way forward. A step still ahead of the
             furthest point stays disabled, which is the part that was right. */
          const isReachable = currentIndex > -1 && i <= furthestIndex;

          return (
            <NavItem
              key={e}
              type="button"
              active={isActive}
              complete={isComplete}
              disabled={!isReachable || isActive}
              aria-current={isActive ? 'step' : undefined}
              onClick={() => navigate(e)}
            >
              {isActive && (
                <StepIndicator
                  id="wizard-steps"
                  data-active-indicator="true"
                />
              )}
              <RightPointer
                fill={
                  isActive || isComplete ? palette.iris : palette.lineStrong
                }
              />
              <Text
                as="span"
                mt={0}
                mb={0}
                ml={8}
                fontWeight={font.weight.medium}
              >
                {getTitlesFromRoute(e)}
              </Text>
            </NavItem>
          );
        })}
      </IndicatorGroup>
    </NavbarContainer>
  );
};
